import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Typography, 
  TextField, 
  Button, 
  Box, 
  Grid, 
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Card,
  CardContent,
  Stack,
  Tooltip
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import SearchableSelect from '../../components/SearchableSelect';
import grupoMcpService, { GrupoMcp } from '../../services/grupoMcpService';
import grupoMcpUsuarioService from '../../services/grupoMcpUsuarioService';
import usuarioService, { Usuario } from '../../services/usuarioService';
import GrupoMcpMediosPagoTab from './GrupoMcpMediosPagoTab';

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`grupo-caja-tabpanel-${index}`}
      aria-labelledby={`grupo-caja-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface GrupoMcpFormData {
  grupoMcp: number;
  descripcion: string;
  plantilla?: string;
  impresora?: string | null;
  vistaPrevia?: string;
  copias?: number | null;
  grupoMcpDestino?: number | null;
}

const defaultForm: GrupoMcpFormData = {
  grupoMcp: 0,
  descripcion: '',
  plantilla: '',
  impresora: null,
  vistaPrevia: 'S',
  copias: 1,
  grupoMcpDestino: null,
};

const GrupoMcpForm: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { grupoMcp } = useParams();
  const [formData, setFormData] = useState<GrupoMcpFormData>({ ...defaultForm });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [gruposMcpList, setGruposMcpList] = useState<GrupoMcp[]>([]);
  const [loadingGrupos, setLoadingGrupos] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [usuariosAsignados, setUsuariosAsignados] = useState<string[]>([]);
  const [usuariosDisponibles, setUsuariosDisponibles] = useState<Usuario[]>([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState<string>('');

  const loadGrupoMcp = useCallback(async () => {
    if (!grupoMcp) return;
    try {
      setLoading(true);
      const grupo = await grupoMcpService.getGrupoMcp(Number(grupoMcp));
      setFormData({
        grupoMcp: grupo.grupoMcp,
        descripcion: grupo.descripcion || '',
        plantilla: grupo.plantilla || '',
        impresora: grupo.impresora ?? null,
        vistaPrevia: grupo.vistaPrevia || 'S',
        copias: grupo.copias ?? 1,
        grupoMcpDestino:
          grupo.grupoMcpDestino != null && grupo.grupoMcpDestino > 0
            ? grupo.grupoMcpDestino
            : null,
      });
    } catch (error) {
      enqueueSnackbar('Error al cargar el grupo de caja', { variant: 'error' });
      navigate('/tesoreria/grupos-mcp');
    } finally {
      setLoading(false);
    }
  }, [grupoMcp, enqueueSnackbar, navigate]);

  const loadGruposMcp = useCallback(async () => {
    try {
      setLoadingGrupos(true);
      const response = await grupoMcpService.getGruposMcp({
        pageNumber: 1,
        pageSize: 1000,
        searchTerm: undefined
      });
      setGruposMcpList(response.items || []);
    } catch (error) {
      console.error('Error al cargar grupos de caja:', error);
      enqueueSnackbar('Error al cargar la lista de grupos de caja', { variant: 'error' });
      setGruposMcpList([]);
    } finally {
      setLoadingGrupos(false);
    }
  }, [enqueueSnackbar]);

  const loadUsuarios = useCallback(async () => {
    try {
      setLoadingUsuarios(true);
      const users = await usuarioService.getUsuarios({ pageNumber: 1, pageSize: 500 });
      setUsuariosDisponibles(Array.isArray(users) ? users : []);
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      enqueueSnackbar('Error al cargar la lista de usuarios', { variant: 'error' });
      setUsuariosDisponibles([]);
    } finally {
      setLoadingUsuarios(false);
    }
  }, [enqueueSnackbar]);

  const loadUsuariosAsignados = useCallback(async () => {
    if (!grupoMcp) {
      setUsuariosAsignados([]);
      return;
    }
    try {
      const asignaciones = await grupoMcpUsuarioService.getByGrupo(Number(grupoMcp));
      setUsuariosAsignados(asignaciones.map(a => a.usuario));
    } catch (error) {
      console.error('Error al cargar usuarios asignados:', error);
      enqueueSnackbar('Error al cargar usuarios asignados', { variant: 'error' });
      setUsuariosAsignados([]);
    }
  }, [grupoMcp, enqueueSnackbar]);

  useEffect(() => {
    loadGruposMcp();
    loadUsuarios();
    if (grupoMcp) {
      loadGrupoMcp();
      loadUsuariosAsignados();
    }
  }, [grupoMcp, loadGrupoMcp, loadGruposMcp, loadUsuarios, loadUsuariosAsignados]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numericFields = new Set(['grupoMcp', 'copias', 'grupoMcpDestino']);
    if (numericFields.has(name)) {
      const parsed = value === '' ? (null as any) : Number(value);
      setFormData(prev => ({ ...prev, [name]: parsed } as any));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const grupoDestinoOptions = useMemo(
    () =>
      gruposMcpList
        .filter((g) => !grupoMcp || g.grupoMcp !== Number(grupoMcp))
        .map((grupo) => ({
          value: grupo.grupoMcp,
          label: `${grupo.grupoMcp} - ${grupo.descripcion?.trim()}`,
        })),
    [gruposMcpList, grupoMcp],
  );

  const usuarioOptions = useMemo(
    () =>
      usuariosDisponibles
        .filter((u) => !usuariosAsignados.includes(u.usuario || u.username || ''))
        .map((user) => {
          const id = user.usuario || user.username || user.id || '';
          const nombre = user.username || user.usuario || user.nombre;
          const email = user.email ? ` (${user.email})` : '';
          return { value: id, label: `${nombre}${email}` };
        })
        .filter((opt) => opt.value),
    [usuariosDisponibles, usuariosAsignados],
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleAgregarUsuario = async () => {
    if (!usuarioSeleccionado || !grupoMcp) {
      enqueueSnackbar('Debe seleccionar un usuario', { variant: 'warning' });
      return;
    }

    if (usuariosAsignados.includes(usuarioSeleccionado)) {
      enqueueSnackbar('El usuario ya está asignado a este grupo', { variant: 'warning' });
      return;
    }

    try {
      await grupoMcpUsuarioService.create({
        grupoMcp: Number(grupoMcp),
        usuario: usuarioSeleccionado
      });
      enqueueSnackbar('Usuario asignado exitosamente', { variant: 'success' });
      setUsuarioSeleccionado('');
      loadUsuariosAsignados();
    } catch (error: any) {
      console.error('Error al asignar usuario:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al asignar usuario', { variant: 'error' });
    }
  };

  const handleEliminarUsuario = async (usuario: string) => {
    if (!grupoMcp) return;
    
    if (!window.confirm(`¿Está seguro de eliminar el usuario ${usuario} de este grupo?`)) {
      return;
    }

    try {
      await grupoMcpUsuarioService.delete(Number(grupoMcp), usuario);
      enqueueSnackbar('Usuario eliminado exitosamente', { variant: 'success' });
      loadUsuariosAsignados();
    } catch (error: any) {
      console.error('Error al eliminar usuario:', error);
      enqueueSnackbar(error?.response?.data?.message || 'Error al eliminar usuario', { variant: 'error' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      const requiredMissing: string[] = [];
      if (!formData.grupoMcp || Number(formData.grupoMcp) <= 0) requiredMissing.push('grupoMcp');
      if (!String(formData.descripcion || '').trim()) requiredMissing.push('descripcion');
      if (requiredMissing.length) {
        enqueueSnackbar(`Complete los campos obligatorios: ${requiredMissing.join(', ')}`, { variant: 'warning' });
        setSaving(false);
        return;
      }

      const payload: GrupoMcp = {
        ...formData,
        grupoMcp: Number(formData.grupoMcp),
        descripcion: String(formData.descripcion).trim(),
        plantilla: formData.plantilla?.trim() || '',
        impresora: formData.impresora?.trim() || null,
        vistaPrevia: formData.vistaPrevia || 'S',
        copias: formData.copias ?? 1,
        grupoMcpDestino:
          formData.grupoMcpDestino != null && formData.grupoMcpDestino > 0
            ? formData.grupoMcpDestino
            : undefined,
      };

      if (grupoMcp) {
        await grupoMcpService.updateGrupoMcp(Number(grupoMcp), payload);
      } else {
        await grupoMcpService.createGrupoMcp(payload);
      }

      enqueueSnackbar('Grupo de caja guardado exitosamente', { variant: 'success' });
      navigate('/tesoreria/grupos-mcp');
    } catch (error: any) {
      let mostrado = false;
      const data = error?.response?.data;
      if (data?.errors && typeof data.errors === 'object') {
        let detalle = '';
        for (const campo in data.errors) {
          if (Array.isArray(data.errors[campo])) {
            const nombreCampo = campo.charAt(0).toLowerCase() + campo.slice(1);
            detalle += `${nombreCampo}: ${data.errors[campo].join('; ')}\n`;
          }
        }
        if (detalle) {
          enqueueSnackbar(`Error en los datos: ${detalle}`, { variant: 'error', autoHideDuration: 12000 });
          mostrado = true;
        }
      }
      const messageText = (data?.message || data || error?.message || '').toString();
      if (!mostrado && messageText) {
        if (
          messageText.includes('PRIMARY KEY') ||
          messageText.toLowerCase().includes('duplicate') ||
          messageText.includes('Violation of PRIMARY KEY constraint')
        ) {
          enqueueSnackbar(`El grupo de caja con código ${formData.grupoMcp} ya existe.`, { variant: 'error' });
          mostrado = true;
        }
      }
      if (!mostrado) enqueueSnackbar('Error al guardar el grupo de caja', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card 
        sx={{ 
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/tesoreria/grupos-mcp')}
              sx={{ textTransform: 'none' }}
            >
              Grupos de Caja
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
              {grupoMcp ? 'Editar Grupo de Caja' : 'Nuevo Grupo de Caja'}
            </Typography>
          </Stack>
          <form onSubmit={handleSubmit}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange} 
                aria-label="pestañas de grupo de caja"
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 500,
                    borderRadius: '8px 8px 0 0',
                  }
                }}
              >
                <Tab label="General" />
                {grupoMcp && <Tab label="Usuarios" />}
                {grupoMcp && <Tab label="Medios de Pago" />}
              </Tabs>
            </Box>

            <TabPanel value={tabValue} index={0}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <TextField 
                    name="grupoMcp" 
                    label="N° Grupo de Caja" 
                    type="number" 
                    value={formData.grupoMcp} 
                    onChange={handleChange} 
                    fullWidth 
                    required 
                    disabled={!!grupoMcp}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField 
                    name="descripcion" 
                    label="Descripción" 
                    value={formData.descripcion} 
                    onChange={handleChange} 
                    fullWidth 
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField 
                    name="plantilla" 
                    label="Plantilla" 
                    value={formData.plantilla || ''} 
                    onChange={handleChange} 
                    fullWidth
                    placeholder="Ej: C:\\GPC\\Reportes\\Tesoreria\\Cierre de Caja.rpt"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="impresora" 
                    label="Impresora" 
                    value={formData.impresora || ''} 
                    onChange={handleChange} 
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SearchableSelect
                    label="Vista Previa"
                    value={formData.vistaPrevia || 'S'}
                    onChange={(val) => setFormData((prev) => ({ ...prev, vistaPrevia: val ?? 'S' }))}
                    options={[
                      { value: 'S', label: 'Sí' },
                      { value: 'N', label: 'No' },
                    ]}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField 
                    name="copias" 
                    label="Copias" 
                    type="number" 
                    value={formData.copias ?? ''} 
                    onChange={handleChange} 
                    fullWidth
                    inputProps={{ min: 1 }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SearchableSelect<number>
                    label="Grupo de Caja Destino para Traspasos"
                    value={formData.grupoMcpDestino ?? null}
                    onChange={(val) =>
                      setFormData((prev) => ({
                        ...prev,
                        grupoMcpDestino: val != null && val > 0 ? val : null,
                      }))
                    }
                    options={grupoDestinoOptions}
                    loading={loadingGrupos}
                    allowEmpty
                    emptyOptionLabel="Ninguno"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            </TabPanel>

            {grupoMcp && (
              <TabPanel value={tabValue} index={1}>
                <Box>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: '#1976d2' }}>
                    Usuarios Asignados al Grupo de Caja
                  </Typography>
                  
                  <Card 
                    sx={{ 
                      mb: 3, 
                      p: 2, 
                      bgcolor: '#fafafa', 
                      borderRadius: 2,
                      border: '1px solid #e0e0e0'
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={8}>
                        <SearchableSelect
                          label="Seleccionar Usuario"
                          value={usuarioSeleccionado || null}
                          onChange={(val) => setUsuarioSeleccionado(val ?? '')}
                          options={usuarioOptions}
                          loading={loadingUsuarios}
                          allowEmpty
                          sx={{
                            '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#ffffff' },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Button
                          variant="contained"
                          startIcon={<AddIcon />}
                          onClick={handleAgregarUsuario}
                          disabled={!usuarioSeleccionado || loadingUsuarios}
                          fullWidth
                          sx={{
                            borderRadius: 2,
                            py: 1.5,
                            textTransform: 'none',
                            fontWeight: 500,
                            boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                            '&:hover': {
                              boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                            }
                          }}
                        >
                          Agregar Usuario
                        </Button>
                      </Grid>
                    </Grid>
                  </Card>

                  {usuariosAsignados.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                      No hay usuarios asignados a este grupo de caja
                    </Typography>
                  ) : (
                    <Card sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
                      <List>
                        {usuariosAsignados.map((usuario) => {
                          const userInfo = usuariosDisponibles.find(u => (u.usuario || u.username) === usuario);
                          return (
                            <ListItem key={usuario} divider>
                              <ListItemText
                                primary={usuario}
                                secondary={userInfo?.email || userInfo?.role || ''}
                              />
                              <ListItemSecondaryAction>
                                <Tooltip title="Eliminar usuario">
                                  <IconButton
                                    edge="end"
                                    aria-label="eliminar"
                                    onClick={() => handleEliminarUsuario(usuario)}
                                    sx={{
                                      bgcolor: '#f44336',
                                      color: '#fff',
                                      borderRadius: 1.5,
                                      '&:hover': { 
                                        bgcolor: '#d32f2f',
                                        transform: 'scale(1.1)',
                                      },
                                      transition: 'all 0.2s'
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </ListItemSecondaryAction>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Card>
                  )}
                </Box>
              </TabPanel>
            )}

            {grupoMcp && (
              <TabPanel value={tabValue} index={2}>
                <GrupoMcpMediosPagoTab grupoMcp={Number(grupoMcp)} />
              </TabPanel>
            )}

            <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
              <Button 
                type="submit" 
                variant="contained" 
                color="primary" 
                disabled={saving}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)',
                  }
                }}
              >
                {saving ? <CircularProgress size={24} /> : 'Guardar'}
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => navigate('/tesoreria/grupos-mcp')} 
                disabled={saving}
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 500,
                }}
              >
                Cancelar
              </Button>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default GrupoMcpForm;

